/**
 * Sprint 12+W5 (12.07.2026) — Legal-Pages als Redirects zur zentralen
 * Rechtstext-Quelle. Legal-Links-Konsistenz-Update (Do10.09.2026, 18:00):
 * Umstellung von aj-kommunikation.de auf die Marken-Domain
 * effektiv-kommunizieren.de (Flex4Success ist Subdomain).
 *
 * Free/Pro (LandingPage.jsx) und Ablefy-Mails (backend/ablefy.py) linken
 * zu:
 *   - https://effektiv-kommunizieren.de/impressum
 *   - https://effektiv-kommunizieren.de/datenschutz
 *   - mailto:hello@aj-kommunikation.de (echte Mailbox, unverändert)
 *
 * Die Warteliste-Footer-Links `/impressum`, `/datenschutz`, `/kontakt`
 * bleiben interne Routen (konsistente Nav-Semantik), leiten aber
 * automatisch auf dieselbe Quelle weiter → Single Source of Truth
 * via `legal/links.js`.
 */
import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ExternalLink, Loader2 } from "lucide-react";
import { LEGAL_LINKS, LEGAL_CONTACT_MAILTO } from "@/legal/links";

function LegalRedirectShell({ title, targetUrl, testId }) {
  useEffect(() => {
    if (typeof window !== "undefined" && targetUrl) {
      // window.location.replace vermeidet History-Eintrag → Browser-Back
      // führt sauber zurück auf /warteliste statt in Endlosschleife.
      window.location.replace(targetUrl);
    }
  }, [targetUrl]);

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ backgroundColor: "#FAFAF7", color: "#1a1a1a" }}
      data-testid={testId}
    >
      <main className="flex-1 flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-lg mx-auto text-center">
          <Loader2
            className="w-8 h-8 mx-auto animate-spin"
            style={{ color: "#64748b" }}
          />
          <h1
            className="mt-6 text-2xl sm:text-3xl font-bold tracking-tight"
            style={{ color: "#1a1a1a" }}
          >
            {title}
          </h1>
          <p className="mt-4 text-base" style={{ color: "#4a4a4a" }}>
            Du wirst weitergeleitet zu unserer offiziellen Rechtstext-Seite …
          </p>
          <p className="mt-6 text-sm" style={{ color: "#64748b" }}>
            Wenn die Weiterleitung nicht funktioniert,{" "}
            <a
              href={targetUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 underline hover:text-black transition-colors"
              style={{ color: "#1a1a1a" }}
              data-testid={`${testId}-manual-link`}
            >
              hier klicken <ExternalLink className="w-3 h-3" />
            </a>
            .
          </p>
          <Link
            to="/warteliste"
            className="mt-10 inline-flex items-center gap-2 text-sm hover:underline"
            style={{ color: "#64748b" }}
            data-testid="legal-back-link"
          >
            <ArrowLeft className="w-4 h-4" />
            Zurück zur Warteliste
          </Link>
        </div>
      </main>
      <footer className="py-6 text-center text-xs" style={{ color: "#94a3b8" }}>
        Flex4Success
      </footer>
    </div>
  );
}

export function ImpressumPage() {
  return (
    <LegalRedirectShell
      title="Impressum"
      targetUrl={LEGAL_LINKS.impressum}
      testId="impressum-page"
    />
  );
}

export function DatenschutzPage() {
  return (
    <LegalRedirectShell
      title="Datenschutz"
      targetUrl={LEGAL_LINKS.datenschutz}
      testId="datenschutz-page"
    />
  );
}

export function KontaktPage() {
  // Kontakt bleibt in-app: mailto:-Trigger, keine Redirect-Schleife.
  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ backgroundColor: "#FAFAF7", color: "#1a1a1a" }}
      data-testid="kontakt-page"
    >
      <main className="flex-1 px-6 py-16 sm:py-20">
        <div className="w-full max-w-2xl mx-auto">
          <Link
            to="/warteliste"
            className="inline-flex items-center gap-2 text-sm mb-8 hover:underline"
            style={{ color: "#64748b" }}
            data-testid="legal-back-link"
          >
            <ArrowLeft className="w-4 h-4" />
            Zurück
          </Link>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-8">
            Kontakt
          </h1>
          <p className="text-base leading-relaxed" style={{ color: "#4a4a4a" }}>
            Für Fragen, Feedback oder rechtliche Anfragen erreichst du uns
            per Mail:
          </p>
          <p className="mt-4">
            <a
              href={LEGAL_CONTACT_MAILTO}
              className="text-lg font-semibold underline"
              style={{ color: "#1a1a1a" }}
              data-testid="kontakt-mailto"
            >
              {LEGAL_LINKS.kontaktMail}
            </a>
          </p>
          <p className="mt-8 text-sm" style={{ color: "#64748b" }}>
            Wir antworten in der Regel innerhalb von 24 Stunden.
          </p>
        </div>
      </main>
      <footer className="py-6 text-center text-xs" style={{ color: "#94a3b8" }}>
        Flex4Success
      </footer>
    </div>
  );
}
